/**
 * Uppy Dashboard + Transloadit upload hook (Uppy v3).
 *
 * Each file creates 1 separate Transloadit assembly (scio1 behavior).
 * In Uppy v3, `assemblyOptions` is called per-file. We inject a unique
 * `fileID` field per file so the dedupe logic creates separate assemblies.
 *
 * The caller provides an `onUploadSuccess` callback to handle save logic,
 * making this hook reusable across features (assets, playlists, etc.).
 *
 * Uppy is built lazily on each open() call — not on mount.
 *
 * scio1 source: imports/ui/services/index.js → openUploadModal(), processUploads()
 * scio2 reference: optisigns-portal-common → transloadit.service.ts
 */
import { useCallback, useRef, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { GET_FILE_UPLOAD_OPTIONS } from '@scio3/graphql-client';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Uppy from '@uppy/core';
import type { UppyFile, Restrictions } from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import Transloadit, { COMPANION_URL, COMPANION_ALLOWED_HOSTS } from '@uppy/transloadit';
import type { AssemblyOptions } from '@uppy/transloadit';
import Webcam from '@uppy/webcam';
import GoogleDrive from '@uppy/google-drive';
import Dropbox from '@uppy/dropbox';
import OneDrive from '@uppy/onedrive';
import en_US from '@uppy/locales/lib/en_US';
import de_DE from '@uppy/locales/lib/de_DE';
import fr_FR from '@uppy/locales/lib/fr_FR';
import es_ES from '@uppy/locales/lib/es_ES';
import it_IT from '@uppy/locales/lib/it_IT';
import nl_NL from '@uppy/locales/lib/nl_NL';
import pt_BR from '@uppy/locales/lib/pt_BR';
import sv_SE from '@uppy/locales/lib/sv_SE';
import { validateImageResolution, validateVideoBitrate } from '../utils/file-validation';
import { showLoadingOverlay } from '@/components/shared';

// --- Types ---

export interface UploadFileMeta {
  name: string;
  type: string;
  size: number;
  extension: string;
}

export interface TransloaditUploadOptions {
  /** Called per file on successful assembly upload. Caller handles save logic. */
  onUploadSuccess: (fileMeta: UploadFileMeta, assemblyId: string, bucket?: string) => void;
  /** Called when all files in a batch are done. */
  onAllComplete?: () => void;
  /** Override for getUploadOptions query variables (e.g. replace asset flow). */
  uploadOptionsPayload?: Record<string, unknown>;
  /** Restrict to single file upload (for replace asset flow). */
  restrictToSingleFile?: boolean;
  /** Custom note text for the Dashboard (overrides default). */
  dashboardNote?: string;
  /** Called when the user closes the Dashboard modal without uploading. */
  onModalClosed?: () => void;
}

// --- Constants ---

const UPPY_LOCALE_MAP: Record<string, typeof en_US> = {
  en: en_US,
  de: de_DE,
  fr: fr_FR,
  es: es_ES,
  it: it_IT,
  nl: nl_NL,
  pt: pt_BR,
  sv: sv_SE,
};

const EMPTY_PAYLOAD: Record<string, unknown> = {};

// --- Helpers ---

function openDashboardModal(uppy: Uppy) {
  const dashboard = uppy.getPlugin('Dashboard');
  if (dashboard && 'openModal' in dashboard) {
    (dashboard as unknown as { openModal: () => void }).openModal();
  }
}

// --- Hook ---

export function useTransloaditUpload({
  onUploadSuccess,
  onAllComplete,
  uploadOptionsPayload = EMPTY_PAYLOAD,
  restrictToSingleFile,
  dashboardNote,
  onModalClosed,
}: TransloaditUploadOptions) {
  const { t, i18n } = useTranslation();
  const uppyRef = useRef<Uppy | null>(null);
  const bucketRef = useRef<string | undefined>(undefined);
  const onUploadSuccessRef = useRef(onUploadSuccess);
  const onAllCompleteRef = useRef(onAllComplete);
  const onModalClosedRef = useRef(onModalClosed);

  // Keep callback refs in sync without re-creating Uppy
  onUploadSuccessRef.current = onUploadSuccess;
  onAllCompleteRef.current = onAllComplete;
  onModalClosedRef.current = onModalClosed;

  const [fetchUploadOptions] = useLazyQuery(GET_FILE_UPLOAD_OPTIONS, {
    fetchPolicy: 'network-only',
  });

  // Cleanup on unmount — pass reason: 'unmount' so Uppy v3 does NOT cancel
  // in-progress Transloadit assemblies still encoding server-side (#506).
  useEffect(() => {
    return () => {
      uppyRef.current?.close({ reason: 'unmount' });
      uppyRef.current = null;
    };
  }, []);

  /** Fetch upload options from backend (network-only, fresh per upload session). */
  const getUploadOptions = useCallback(async (): Promise<Record<string, unknown>> => {
    const { data, error } = await fetchUploadOptions({
      variables: { payload: uploadOptionsPayload },
    });

    if (error) throw new Error(error.message);

    const result = data as Record<string, unknown> | undefined;
    const opts = result?.getFileUploadOptions as Record<string, unknown>;
    if (!opts) throw new Error(t('Failed to get upload options'));

    return opts;
  }, [fetchUploadOptions, uploadOptionsPayload, t]);

  /** Build a fresh Uppy instance with Dashboard + Transloadit + providers. */
  const buildUppy = useCallback(
    (opts: Record<string, unknown>): Uppy => {
      // Destroy previous instance without cancelling in-flight Transloadit assemblies.
      // close({ reason: 'unmount' }) uninstalls plugins + cleans up DOM/listeners,
      // but Transloadit plugin skips assembly cancellation when reason !== 'user' (#506).
      if (uppyRef.current) {
        uppyRef.current.close({ reason: 'unmount' });
        uppyRef.current = null;
      }

      const restrictions: Restrictions = {
        ...(opts.restrictions as Restrictions),
        ...(restrictToSingleFile && { maxNumberOfFiles: 1 }),
      };

      const uppy = new Uppy({
        restrictions,
        allowMultipleUploads: false,
        onBeforeUpload: (files) => {
          // Inject fileID into meta for each file (scio1/scio2 pattern)
          const updated: typeof files = {};
          for (const [fileID, file] of Object.entries(files)) {
            updated[fileID] = {
              ...file,
              meta: { ...file.meta, fileID },
            };
          }
          return updated;
        },
      });

      // Set locale after construction (scio2 pattern)
      const locale = UPPY_LOCALE_MAP[i18n.language] || en_US;
      uppy.setOptions({ locale });

      // Dashboard — modal mode
      const isDark = document.documentElement.classList.contains('dark');
      uppy.use(Dashboard, {
        inline: false,
        target: 'body',
        proudlyDisplayPoweredByUppy: false,
        theme: isDark ? 'dark' : 'light',
        note: dashboardNote || t('Images, Documents, Audio and Video only'),
        metaFields: [{ id: 'name', name: t('File name'), placeholder: t('Enter file name') }],
      });

      // Transloadit — 1 assembly per file.
      // In Uppy v3, assemblyOptions is called per-file. The backend returns
      // fields: ["name", "fileID"] as an array. Uppy resolves each field name
      // from file.meta, producing unique values per file (dedupe creates separate assemblies).
      uppy.use(Transloadit, {
        assemblyOptions: (_file: UppyFile): AssemblyOptions => {
          // Backend returns fields as an array of meta field names, e.g. ["name", "fileID"].
          // Uppy's Transloadit plugin resolves arrays by extracting values from file.meta
          // per file (see getFields in AssemblyOptions.js). This produces unique field values
          // per file (different name, different fileID), ensuring 1 assembly per file.
          // onBeforeUpload already injects fileID into file.meta for this to work.
          // IMPORTANT: Do NOT spread the array into an object — that produces { "0": "name", "1": "fileID" }.
          return structuredClone(opts) as AssemblyOptions;
        },
        waitForEncoding: false,
        waitForMetadata: false,
        limit: 2, // Max concurrent assemblies (scio1 parity — prevents large-file cancellation)
      });

      // Cloud providers (from server opts.providers)
      const providers = (opts.providers as string[]) || [];

      if (providers.includes('webcam')) {
        uppy.use(Webcam, { target: Dashboard });
      }
      if (providers.includes('google-drive')) {
        uppy.use(GoogleDrive, {
          target: Dashboard,
          companionUrl: COMPANION_URL,
          companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        });
      }
      if (providers.includes('dropbox')) {
        uppy.use(Dropbox, {
          target: Dashboard,
          companionUrl: COMPANION_URL,
          companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        });
      }
      if (providers.includes('onedrive')) {
        uppy.use(OneDrive, {
          target: Dashboard,
          companionUrl: COMPANION_URL,
          companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        });
      }

      // --- Events (follows scio2 _openUploadModal pattern) ---

      // Client-side validation on file add
      uppy.on('file-added', async (file: UppyFile) => {
        const blob = file.data as File;

        const resolutionError = await validateImageResolution(blob);
        if (resolutionError) {
          toast.error(resolutionError.message);
          uppy.removeFile(file.id);
          return;
        }

        const bitrateError = await validateVideoBitrate(blob);
        if (bitrateError) {
          toast.error(bitrateError.message);
          uppy.removeFile(file.id);
          return;
        }
      });

      // Per-file success — extract assembly ID and call caller's callback.
      // scio2: uppy.on('upload-success', (upload) => { _processUploads([upload], ...) })
      uppy.on('upload-success', (file: UppyFile | undefined) => {
        if (!file) return;
        const fileWithTransloadit = file as UppyFile & {
          transloadit?: { assembly?: string };
        };
        const assemblyId = fileWithTransloadit.transloadit?.assembly || '';

        // Use meta.name (edited by user via metaFields) over original file.name
        const name = (file.meta?.name as string) || file.name || '';
        const extension = name.includes('.') ? name.split('.').pop() || '' : '';

        onUploadSuccessRef.current(
          {
            name,
            type: file.type || '',
            size: file.size || 0,
            extension,
          },
          assemblyId,
          bucketRef.current,
        );
      });

      // All files complete — handle failures, close modal (scio2 pattern).
      // Do NOT call uppy.close()/destroy() — it triggers cancelAll() which cancels
      // Transloadit assemblies still processing server-side.
      // Instance is destroyed on next open() call in buildUppy().
      uppy.on('complete', (result) => {
        const { failed } = result;

        if (failed && failed.length > 0) {
          const names = failed.map((file) => (file as UppyFile).name).join(', ');
          toast.error(t('Failed to upload {{names}}.', { names }));
        }

        onAllCompleteRef.current?.();

        // Close modal (scio2: modal.closeModal())
        const dashboard = uppy.getPlugin('Dashboard');
        if (dashboard && 'closeModal' in dashboard) {
          (dashboard as unknown as { closeModal: () => void }).closeModal();
        }
      });

      // Dashboard closed without completing upload — notify caller
      uppy.on('dashboard:modal-closed' as 'complete', () => {
        onModalClosedRef.current?.();
      });

      uppyRef.current = uppy;
      return uppy;
    },
    [i18n.language, t, restrictToSingleFile, dashboardNote],
  );

  /** Build a fresh Uppy instance each time (ensures clean state per session). */
  const buildFreshUppy = useCallback(async (): Promise<Uppy> => {
    // Don't close() old instance — it cancels in-flight Transloadit assemblies (#506).
    // buildUppy() handles detaching the previous modal UI.

    const opts = await getUploadOptions();
    bucketRef.current = (opts.bucket as string) || undefined;
    return buildUppy(opts);
  }, [getUploadOptions, buildUppy]);

  /** Open the Uppy Dashboard modal. Builds a fresh Uppy instance each time. */
  const open = useCallback(async () => {
    const dismissLoading = showLoadingOverlay(300);
    try {
      const uppy = await buildFreshUppy();
      dismissLoading();
      openDashboardModal(uppy);
    } catch (err) {
      dismissLoading();
      const message = err instanceof Error ? err.message : t('Failed to open Upload Modal.');
      toast.error(message);
    }
  }, [buildFreshUppy, t]);

  /** Add files to Uppy (e.g. from drag-drop) then open the modal. */
  const openWithFiles = useCallback(
    async (files: File[]) => {
      const dismissLoading = showLoadingOverlay(300);
      try {
        const uppy = await buildFreshUppy();

        for (const file of files) {
          try {
            uppy.addFile({
              name: file.name,
              type: file.type,
              data: file,
              source: 'Local',
              isRemote: false,
            });
          } catch {
            // Uppy throws if file doesn't match restrictions — handled by Uppy informer
          }
        }

        dismissLoading();
        openDashboardModal(uppy);
      } catch (err) {
        dismissLoading();
        const message = err instanceof Error ? err.message : t('Failed to open Upload Modal.');
        toast.error(message);
      }
    },
    [buildFreshUppy, t],
  );

  return { open, openWithFiles };
}
